import { useEffect } from 'react';
import useGetApi from '../useGetApi';
import {
  RebatePoint,
  UnifiedForecastResult,
  CalculationType,
} from '@erp/shared-types';

// Unified rebate forecasting interface

interface ForecastParams {
  agreementId?: string;
  startDate?: string;
  endDate?: string;
  /**
   * Optional filter for calculation types to fetch.
   * Useful for YOY comparison where only 'estimate' and 'historical' are needed.
   */
  calculationTypes?: CalculationType[];
}

// Unified rebate forecast hook - replaces both useRebateForecast and useCalendarYearForecast
export const useRebateForecast = (params: ForecastParams = {}) => {
  // Normalize calculationTypes array to comma-separated string for backend
  const normalizedParams = {
    ...params,
    ...(params.calculationTypes && params.calculationTypes.length > 0
      ? { calculationTypes: params.calculationTypes.join(',') }
      : { calculationTypes: undefined }),
  };

  return useGetApi<UnifiedForecastResult>({
    endpoint: 'rebates/forecast',
    queryParameters: normalizedParams,
    options: {
      staleTime: 0, // Always check backend for fresh data
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
    transformResponse: (data) => {
      if (!data) return data;

      // Parse date strings to Date objects for proper typing

      // Handle new structure with separate actuals and predictions
      if (data.estimates) {
        data.estimates = data.estimates.map((point: RebatePoint) => ({
          ...point,
          date: new Date(point.date),
        }));
      }

      if (data.predictions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const transformedPredictions: any = {};
        Object.entries(data.predictions).forEach(([method, points]) => {
          if (Array.isArray(points)) {
            transformedPredictions[method] = points.map(
              (point: RebatePoint) => ({
                ...point,
                date: new Date(point.date),
              }),
            );
          }
        });
        data.predictions = transformedPredictions;
      }

      // Parse reported date strings to Date objects
      if (data.reported) {
        data.reported = data.reported.map((point: RebatePoint) => ({
          ...point,
          date: new Date(point.date),
        }));
      }

      // Parse forecast period dates
      if (data.forecastPeriod) {
        data.forecastPeriod = {
          ...data.forecastPeriod,
          startDate: new Date(data.forecastPeriod.startDate),
          endDate: new Date(data.forecastPeriod.endDate),
        };
      }

      // Parse forecast dates array
      if (data.forecastDates) {
        data.forecastDates = data.forecastDates.map((dateValue) =>
          typeof dateValue === 'string' ? new Date(dateValue) : dateValue,
        );
      }

      return data;
    },
  });
};

// New hook that auto-generates forecast if stale
export const useAgreementForecastAutoGenerate = (
  agreementId: string,
  params?: { startDate?: string; endDate?: string },
  onSuccess?: (data: UnifiedForecastResult) => void,
) => {
  const queryResult = useGetApi<UnifiedForecastResult>({
    endpoint: `rebates/forecast/agreements/${agreementId}`,
    queryParameters: params,
    options: {
      staleTime: 0, // Always check backend for staleness
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!agreementId, // Note: dates are optional, backend will default to current year if not provided
    },
    transformResponse: (data: UnifiedForecastResult) => {
      if (!data) return data;

      // Parse date strings to Date objects for proper typing
      // Handle new structure with separate actuals and predictions
      if (data.estimates) {
        data.estimates = data.estimates.map((point: RebatePoint) => ({
          ...point,
          date: new Date(point.date),
        }));
      }

      if (data.predictions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const transformedPredictions: any = {};
        Object.entries(data.predictions).forEach(([method, points]) => {
          if (Array.isArray(points)) {
            transformedPredictions[method] = points.map(
              (point: RebatePoint) => ({
                ...point,
                date: new Date(point.date),
              }),
            );
          }
        });
        data.predictions = transformedPredictions;
      }

      // Parse reported date strings to Date objects
      if (data.reported) {
        data.reported = data.reported.map((point: RebatePoint) => ({
          ...point,
          date: new Date(point.date),
        }));
      }

      // Parse forecast period dates
      if (data.forecastPeriod) {
        data.forecastPeriod = {
          ...data.forecastPeriod,
          startDate: new Date(data.forecastPeriod.startDate),
          endDate: new Date(data.forecastPeriod.endDate),
        };
      }

      // Parse forecast dates array
      if (data.forecastDates) {
        data.forecastDates = data.forecastDates.map((dateValue) =>
          typeof dateValue === 'string' ? new Date(dateValue) : dateValue,
        );
      }

      return data;
    },
  });

  // Call onSuccess callback when data is successfully fetched
  // Note: React Query v5 removed onSuccess from useQuery options, so we use useEffect instead
  useEffect(() => {
    if (
      queryResult.data &&
      !queryResult.isLoading &&
      !queryResult.isError &&
      onSuccess
    ) {
      onSuccess(queryResult.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data, queryResult.isLoading, queryResult.isError]);
  // Note: onSuccess is intentionally excluded from deps to avoid calling it on every callback reference change

  return queryResult;
};

export const useCalendarYearForecast = (year?: number) => {
  const params: ForecastParams = {};

  if (year !== undefined) {
    params.startDate = `${year}-01-01`;
    params.endDate = `${year}-12-31`;
  }

  return useRebateForecast(params);
};
